/**
 * TriggerSettingsModal.tsx — the ⚡ trigger EDITOR (the trigger contract's
 * UI half, node-scoped or graph-wide).
 *
 * Opened from the NodeModal's footer ("Trigger settings", next to "Agent
 * settings" — trigger settings live WHERE the trigger triggers). The
 * TriggersPanel (the graph-level "Triggers" modal) embeds the same
 * EDITOR inline instead of stacking this modal.
 *
 *   - the LIST: every trigger in scope with its live status (last fired /
 *     last error / armed), Fire-now, arm/disarm, delete, edit;
 *   - the EDITOR (TriggerEditor): a condition BUILDER (signal dropdown →
 *     operator → value — generates the CEL expr; free text as advanced
 *     mode), target workflow, cooldown, run input — plus a live DRY-RUN
 *     over the captured tick history (the same walker + firing semantics
 *     the backend's relay path uses), so "would this ever fire?" is
 *     answered while you type.
 *
 * Writes go through the caller's onChange (the setWorkflowTriggers op —
 * whole-list replace).
 */
import { useEffect, useMemo, useState } from 'react';
import { Lightning as Bolt, Plus, Trash, Play } from '@phosphor-icons/react';

import type { ScenarioBundle, TickPayload, WorkflowTrigger } from '../engine/types';
import { cronScheduleError } from '../projects/cronApi';
import { Modal } from './Modal';
import type { RemoteProjectAdapter } from '../projects/RemoteProjectAdapter';
import type { TriggerStatusMap } from './TriggersPanel';
import {
  TRIGGER_OPERATORS, type TriggerOperator,
  buildConditionExpr, dryRunTrigger, parseConditionExpr, signalOptions,
} from '../workflow/triggerTools';

export interface TriggerSettingsModalProps {
  /** Modal title (e.g. "Trigger settings — Anomaly z"). */
  title: string;
  /** The triggers IN SCOPE (the node's rows, or the graph's whole list). */
  triggers: WorkflowTrigger[];
  /** Graph nodes for the trigger's source attribution (the ⚡ badge +
   *  node-modal scoping). Absent = node-scoped (sourceNode pinned by the
   *  caller); present = graph-level management with a node picker. */
  nodeOptions?: { id: string; label?: string }[];
  /** The bundle — signal options + the write merge. */
  bundle: ScenarioBundle;
  editable: boolean;
  projectAdapter?: RemoteProjectAdapter;
  projectId?: string;
  recentTicks: TickPayload[];
  triggerStatus: TriggerStatusMap;
  /** Fire a workflow run NOW. Optional — absent (not connected) hides the
   *  Fire buttons; everything else still works. */
  fireTrigger?: (workflow: string, input: unknown[]) => Promise<void>;
  onClose: () => void;
  /** Commit the scope's next trigger list (the caller merges into the full
   *  graph list and dispatches the setWorkflowTriggers op). */
  onChange: (next: WorkflowTrigger[]) => void;
}

interface Draft {
  id: string;
  workflow: string;
  on: 'expr' | 'manual' | 'cron';
  expr: string;
  schedule: string;
  label: string;
  sourceNode: string;
  cooldownTicks: number;
  inputJson: string;
  enabled: boolean;
  useBuilder: boolean;
  signal: string;
  op: TriggerOperator;
  value: string;
}

function draftFrom(t: WorkflowTrigger, fallbackWf: string): Draft {
  const parsed = t.on === 'expr' && t.expr ? parseConditionExpr(t.expr) : null;
  return {
    id: t.id,
    workflow: t.workflow || fallbackWf,
    on: t.on,
    expr: t.expr ?? '',
    schedule: t.schedule ?? '',
    label: t.label ?? '',
    sourceNode: t.sourceNode ?? '',
    cooldownTicks: t.cooldownTicks ?? 96,
    inputJson: t.input ? JSON.stringify(t.input) : '',
    enabled: t.enabled !== false,
    useBuilder: parsed !== null,
    signal: parsed?.signal ?? 'step',
    op: parsed?.op ?? '>',
    value: parsed?.value ?? '0',
  };
}

function emptyDraft(id: string, fallbackWf: string): Draft {
  return {
    id, workflow: fallbackWf, on: 'expr', expr: '', schedule: '', label: '', sourceNode: '',
    cooldownTicks: 96, inputJson: '', enabled: true,
    useBuilder: true, signal: 'step', op: '>', value: '0',
  };
}

function draftToTrigger(d: Draft): WorkflowTrigger {
  let input: unknown[] | undefined;
  if (d.inputJson.trim()) {
    try {
      const parsed = JSON.parse(d.inputJson);
      input = Array.isArray(parsed) ? parsed : [parsed];
    } catch { /* invalid JSON — drop the input rather than block the save */ }
  }
  return {
    id: d.id,
    workflow: d.workflow,
    on: d.on,
    expr: d.on === 'expr' ? d.expr.trim() || undefined : undefined,
    schedule: d.on === 'cron' ? d.schedule.trim() || undefined : undefined,
    label: d.label || undefined,
    sourceNode: d.sourceNode || undefined,
    cooldownTicks: d.cooldownTicks > 0 ? d.cooldownTicks : undefined,
    input,
    enabled: d.enabled ? undefined : false,
  };
}

/** The trigger editor BODY (no Modal wrapper) — embedded inline by the
 *  TriggersPanel ("Triggers") and rendered as this modal's editing
 *  view. Owns the workflow-name fetch + signal options; commits a single
 *  finished trigger through onSave. */
export function TriggerEditor({
  initial, bundle, editable, projectAdapter, projectId, nodeOptions,
  recentTicks, onSave, onCancel,
}: {
  /** The trigger being edited; absent = creating a new one. */
  initial?: WorkflowTrigger;
  bundle: ScenarioBundle;
  editable: boolean;
  projectAdapter?: RemoteProjectAdapter;
  projectId?: string;
  nodeOptions?: { id: string; label?: string }[];
  recentTicks: TickPayload[];
  onSave: (t: WorkflowTrigger) => void;
  onCancel: () => void;
}): JSX.Element {
  const [wfNames, setWfNames] = useState<string[] | null>(null);
  const [draft, setDraft] = useState<Draft>(() =>
    initial
      ? draftFrom(initial, 'refit')
      : emptyDraft(`trg_${Date.now().toString(36)}`, 'refit'));

  useEffect(() => {
    if (!projectAdapter || !projectId || wfNames !== null) return;
    let alive = true;
    projectAdapter.listWorkflows(projectId)
      .then((ws) => { if (alive) setWfNames(ws.map((w) => w.id)); })
      .catch(() => { if (alive) setWfNames([]); });
    return () => { alive = false; };
  }, [projectAdapter, projectId, wfNames]);

  const lastTick = recentTicks.length > 0 ? recentTicks[recentTicks.length - 1] : null;
  const signals = useMemo(
    () => signalOptions(
      bundle,
      (bundle.metrics ?? []).map((m) => m.key),
      Object.keys(lastTick?.params ?? {}),
      lastTick,
    ),
    [bundle, lastTick],
  );

  const d = draft;
  const set = (p: Partial<Draft>) => setDraft({ ...d, ...p });
  const builderExpr = buildConditionExpr(d.signal, d.op, d.value);
  const effectiveExpr = d.useBuilder ? builderExpr : d.expr;
  const dry = d.on === 'expr' && effectiveExpr.trim()
    ? dryRunTrigger(effectiveExpr, recentTicks, d.cooldownTicks)
    : null;
  const save = () => onSave(draftToTrigger({ ...d, expr: effectiveExpr }));

  return (
    <div className="trigger-edit">
      <div className="trigger-grid">
        <label className="settings-label">
          Target workflow
          <select className="settings-select" value={d.workflow} disabled={!editable}
                  onChange={(e) => set({ workflow: e.target.value })}>
            {wfNames === null && <option value={d.workflow}>{d.workflow}</option>}
            {(wfNames ?? []).map((n) => <option key={n} value={n}>{n}</option>)}
            {wfNames !== null && !wfNames.includes(d.workflow) && (
              <option value={d.workflow}>{d.workflow}</option>
            )}
          </select>
        </label>
        <label className="settings-label">
          Label
          <input className="settings-input" type="text" value={d.label} disabled={!editable}
                 placeholder="What this trigger is for"
                 onChange={(e) => set({ label: e.target.value })} />
        </label>
        {nodeOptions && (
          <label className="settings-label">
            Source node
            <select className="settings-select" value={d.sourceNode} disabled={!editable}
                    onChange={(e) => set({ sourceNode: e.target.value })}>
              <option value="">(none — graph-level)</option>
              {nodeOptions.map((n) => (
                <option key={n.id} value={n.id}>{n.label || n.id}</option>
              ))}
            </select>
          </label>
        )}
        <label className="settings-label">
          Fires
          <select className="settings-select" value={d.on} disabled={!editable}
                  onChange={(e) => set({ on: e.target.value as 'expr' | 'manual' | 'cron' })}>
            <option value="expr">on condition</option>
            <option value="cron">on schedule (cron)</option>
            <option value="manual">manual only (button)</option>
          </select>
        </label>
        {d.on === 'expr' && (
          <label className="settings-label">
            Cooldown (ticks)
            <input className="settings-input" type="number" min={1} value={d.cooldownTicks}
                   disabled={!editable}
                   onChange={(e) => set({ cooldownTicks: Math.max(1, Number(e.target.value) || 96) })} />
          </label>
        )}
      </div>

      {d.on === 'cron' && (
        <div className="modal-section">
          <div className="modal-subhead">Schedule — fires when the clock says</div>
          <input className="settings-input" type="text" value={d.schedule} disabled={!editable}
                 placeholder="0 9 * * *"
                 title="5-field cron: minute hour day-of-month month day-of-week (server local time)"
                 onChange={(e) => set({ schedule: e.target.value })} />
          <div className="trigger-dryrun">
            {d.schedule.trim()
              ? cronScheduleError(d.schedule)
                ? <span className="trigger-dryrun-error">{cronScheduleError(d.schedule)}</span>
                : <>A platform cron job calls this server on <code>{d.schedule.trim()}</code> —
                   the schedule is synced when the trigger is saved (Settings → Cron shows the jobs).</>
              : <span className="param-hint">5 fields: minute hour day month weekday (server local time).</span>}
          </div>
        </div>
      )}

      {d.on === 'expr' && (
        <div className="modal-section">
          <div className="modal-subhead">Condition — fires when this holds</div>
          {d.useBuilder ? (
            <div className="trigger-grid">
              <select className="settings-select" value={d.signal} disabled={!editable}
                      onChange={(e) => set({ signal: e.target.value })}>
                {!signals.some((s) => s.path === d.signal) && (
                  <option value={d.signal}>{d.signal}</option>
                )}
                {signals.map((s) => <option key={s.path} value={s.path}>{s.label}</option>)}
              </select>
              <select className="settings-select" value={d.op} disabled={!editable}
                      onChange={(e) => set({ op: e.target.value as TriggerOperator })}>
                {TRIGGER_OPERATORS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              <input className="settings-input" type="number" step="any" value={d.value}
                     disabled={!editable} onChange={(e) => set({ value: e.target.value })} />
            </div>
          ) : (
            <input className="settings-input" type="text" value={d.expr} disabled={!editable}
                   placeholder="CEL expr, e.g. nodes.anomaly.stock > 2"
                   title="nodes.<id>.stock/.inflow/.outflow/.state.<k>, edges.<id>.flow, metrics.<k>, params.<path>, step"
                   onChange={(e) => set({ expr: e.target.value })} />
          )}
          <div className="trigger-expr-row">
            <code className="trigger-expr-preview">
              {d.useBuilder ? builderExpr : '(free-text expr)'}
            </code>
            {editable && (
              <button type="button" className="btn btn-mini" onClick={() => {
                if (d.useBuilder) set({ expr: builderExpr, useBuilder: false });
                else {
                  const p = parseConditionExpr(d.expr);
                  set(p ? { ...p, useBuilder: true }
                       : { signal: 'step', op: '>', value: '0', useBuilder: true });
                }
              }}>
                {d.useBuilder ? 'Advanced' : 'Builder'}
              </button>
            )}
          </div>
          {dry && (
            <div className="trigger-dryrun">
              {dry.error
                ? <span className="trigger-dryrun-error">expr error: {dry.error}</span>
                : <>
                    Over the last {recentTicks.length} ticks: holds at {dry.holds.length} step(s),
                    would fire <strong>{dry.fires.length}</strong> time(s)
                    {dry.fires.length > 0 && <> — steps {dry.fires.slice(0, 10).join(', ')}{dry.fires.length > 10 ? '…' : ''}</>}
                    {dry.current ? ' · <strong>holds NOW</strong>' : ''}
                  </>}
            </div>
          )}
        </div>
      )}

      <label className="settings-label">
        Run input (JSON, optional)
        <input className="settings-input" type="text" value={d.inputJson} disabled={!editable}
               placeholder='[{"reason": "manual"}]'
               onChange={(e) => set({ inputJson: e.target.value })} />
      </label>

      <div className="modal-footer-row">
        <label className="trigger-armed-toggle">
          <input type="checkbox" checked={d.enabled} disabled={!editable}
                 onChange={(e) => set({ enabled: e.target.checked })} />
          Armed
        </label>
        <span className="flex-spacer" />
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary"
                disabled={!editable || (d.on === 'cron' && !!cronScheduleError(d.schedule))}
                onClick={save}>
          Save trigger
        </button>
      </div>
    </div>
  );
}

export function TriggerSettingsModal({
  title, triggers, bundle, editable, projectAdapter, projectId, nodeOptions,
  recentTicks, triggerStatus, fireTrigger, onClose, onChange,
}: TriggerSettingsModalProps): JSX.Element {
  const [editingIdx, setEditingIdx] = useState<number | null>(null); // -1 = create
  const [fireError, setFireError] = useState<string | null>(null);
  const [firing, setFiring] = useState<string | null>(null);

  const fire = (t: WorkflowTrigger) => {
    if (!fireTrigger) return;
    setFireError(null);
    setFiring(t.id);
    fireTrigger(t.workflow, t.input ?? [])
      .catch((e) => setFireError(`${t.id}: ${e instanceof Error ? e.message : String(e)}`))
      .finally(() => setFiring(null));
  };

  // ─── Editor view (same modal — the editor replaces the list) ─────────────
  if (editingIdx !== null) {
    return (
      <Modal
        title={editingIdx < 0 ? `${title} — new trigger` : `${title} — edit trigger`}
        size="wide"
        onClose={() => setEditingIdx(null)}
      >
        <TriggerEditor
          initial={editingIdx >= 0 ? triggers[editingIdx] : undefined}
          bundle={bundle}
          editable={editable}
          projectAdapter={projectAdapter}
          projectId={projectId}
          nodeOptions={nodeOptions}
          recentTicks={recentTicks}
          onCancel={() => setEditingIdx(null)}
          onSave={(next) => {
            if (editingIdx < 0) onChange([...triggers, next]);
            else onChange(triggers.map((t, j) => (j === editingIdx ? next : t)));
            setEditingIdx(null);
          }}
        />
      </Modal>
    );
  }

  // ─── List view ───────────────────────────────────────────────────────────
  return (
    <Modal title={title} size="wide" onClose={onClose}>
      <div className="trigger-list">
        {fireError && <p className="trigger-fire-error">{fireError}</p>}
        {triggers.length === 0 && (
          <p className="agents-empty">No triggers here yet — add one below.</p>
        )}
        {triggers.map((t, i) => {
          const st = triggerStatus[t.id];
          const disarmed = t.enabled === false;
          return (
            <div key={t.id} className="trigger-row">
              <button
                type="button" className="code-task-btn" disabled={!editable}
                title={disarmed ? 'Disarmed — click to arm' : 'Armed — click to disarm'}
                style={{ opacity: disarmed ? 0.4 : 1 }}
                onClick={() => onChange(triggers.map((x, j) =>
                  j === i ? { ...x, enabled: disarmed ? undefined : false } : x))}
              >
                <Bolt size={14} />
              </button>
              <button
                type="button" className="trigger-row-main"
                disabled={!editable}
                title={editable ? 'Edit' : 'View (read-only)'}
                onClick={() => setEditingIdx(i)}
              >
                <div className="trigger-row-title">{t.label || t.id} → {t.workflow}</div>
                <div className="trigger-row-meta">
                  {t.on === 'expr'
                    ? <code>{t.expr}</code>
                    : t.on === 'cron'
                      ? <><span className="agents-mode">cron</span> <code>{t.schedule}</code></>
                      : <span className="agents-mode">manual</span>}
                  {disarmed && <span className="agents-fence read-only">disarmed</span>}
                  {st?.lastError
                    ? <span className="trigger-status-error" title={st.lastError}>last fire FAILED</span>
                    : st?.lastFiredStep != null
                      ? <span>fired @ step {st.lastFiredStep}</span>
                      : <span className="param-hint">never fired</span>}
                </div>
              </button>
              {fireTrigger && (
                <button
                  type="button" className="code-task-btn" title="Fire now"
                  disabled={firing === t.id}
                  onClick={() => fire(t)}
                >
                  <Play size={14} />
                </button>
              )}
              {editable && (
                <button
                  type="button" className="code-task-btn danger" title="Delete"
                  onClick={() => onChange(triggers.filter((_, j) => j !== i))}
                >
                  <Trash size={13} />
                </button>
              )}
            </div>
          );
        })}
        {editable && (
          <button
            type="button" className="btn agents-add"
            onClick={() => setEditingIdx(-1)}
          >
            <Plus size={14} /> Add trigger
          </button>
        )}
      </div>
    </Modal>
  );
}
